/**
 * Glass Penalty Validator
 *
 * Calculates the thermodynamic penalty of uninsulated glass surfaces
 * in a sauna and validates whether the current heater is sufficient.
 *
 * All constants are read from the Rule Registry.
 */
import { selectHeaterStep } from './heaterSizing';
import { getRuleValue, type RuleOverrides } from '../../data/ruleRegistry';
import type { WallOpening } from '../framing/generateFramedWall';

export interface GlassPenaltyInput {
    physicalVolumeM3: number;
    openings: WallOpening[];
    currentHeaterKw: number;
    precomputedEffectiveVolumeM3?: number;
    precomputedRequiredKw?: number;
    /**
     * Glass already resolved to the hot room (see `resolveHeaterGlassArea`).
     * Without it every window counts, which is right only for one room.
     */
    precomputedGlassAreaSqm?: number;
}

export interface GlassPenaltyResult {
    totalGlassAreaSqm: number;
    effectiveVolumeM3: number;
    requiredKw: number;
    isHeaterSufficient: boolean;
    warningMessage: string | null;
    /** Translation key for the warning; `{kw}` takes `requiredKw`. Null when sufficient. */
    warningKey: 'domain_heater_glassPenaltyWarning' | null;
}

/**
 * Sum up the total glass area from all glazed window openings.
 * Doors and non-glazed openings are excluded.
 */
function sumGlassArea(openings: WallOpening[]): number {
    return openings
        .filter((op) => op.type === 'window' || op.glazed === true)
        .reduce((sum, op) => sum + (op.widthMm / 1000) * (op.heightMm / 1000), 0);
}

export function validateGlassPenalty(
    input: GlassPenaltyInput,
    overrides: RuleOverrides = {},
): GlassPenaltyResult {
    const {
        physicalVolumeM3,
        openings,
        currentHeaterKw,
        precomputedEffectiveVolumeM3,
        precomputedRequiredKw,
        precomputedGlassAreaSqm,
    } = input;

    const coldSurfaceFactor = getRuleValue<number>('heater.coldSurfaceFactor', overrides);
    const warningTemplate = getRuleValue<string>('heater.glassPenaltyWarningText', overrides);

    const totalGlassAreaSqm = precomputedGlassAreaSqm ?? sumGlassArea(openings);
    const effectiveVolumeM3 = precomputedEffectiveVolumeM3 ?? physicalVolumeM3 + totalGlassAreaSqm * coldSurfaceFactor;

    const requiredKw =
        precomputedRequiredKw ?? selectHeaterStep(effectiveVolumeM3, overrides).kw;

    const isHeaterSufficient = currentHeaterKw >= requiredKw;

    const warningMessage = isHeaterSufficient
        ? null
        : warningTemplate.replace('{kw}', String(requiredKw));

    return {
        totalGlassAreaSqm: Number(totalGlassAreaSqm.toFixed(2)),
        effectiveVolumeM3: Number(effectiveVolumeM3.toFixed(1)),
        requiredKw,
        isHeaterSufficient,
        warningMessage,
        warningKey: isHeaterSufficient ? null : 'domain_heater_glassPenaltyWarning',
    };
}
